import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit, Trash2, X, LogIn, Search } from 'lucide-react';

const UsersTab = ({ 
    users,
    filteredUsers,
    filter,
    setFilter,
    userRoles,
    selectedUsers,
    currentUser,
    onSelectAll,
    onSelectUser,
    onRoleChange,
    onDelete,
    onLoginAs,
    onBulkRoleChange,
    onBulkDelete,
    onClearSelection
}) => {
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or email..."
                        value={filter.search || ''}
                        onChange={(e) => setFilter({...filter, search: e.target.value})}
                        className="pl-9 bg-[#0d2847] border-white/10"
                        data-testid="users-search-input"
                    />
                </div>
                <Select value={filter.role} onValueChange={(v) => setFilter({...filter, role: v})}>
                    <SelectTrigger className="w-48 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Roles</SelectItem>
                        {userRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                </Select>
                
                {/* Bulk Actions */}
                {selectedUsers.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30">
                        <span className="text-sm text-primary font-medium">
                            {selectedUsers.length} selected
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkRoleChange}
                            className="text-primary hover:text-primary hover:bg-primary/20 h-7 text-xs"
                            data-testid="bulk-change-role-btn"
                        >
                            <Edit className="w-3 h-3 mr-1" />
                            Change Role
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkDelete}
                            className="text-destructive hover:text-destructive hover:bg-destructive/20 h-7 text-xs"
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onClearSelection}
                            className="text-muted-foreground hover:text-white h-7 text-xs"
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                )}
                
                <span className="text-sm text-muted-foreground ml-auto">
                    Showing {filteredUsers.length} of {users.length} users
                </span>
            </div>

            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left p-4 w-12">
                                <input
                                    type="checkbox"
                                    checked={filteredUsers.filter(u => u.role !== 'admin').length > 0 && 
                                             filteredUsers.filter(u => u.role !== 'admin').every(u => selectedUsers.includes(u.id))}
                                    onChange={(e) => onSelectAll(e.target.checked)}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                    data-testid="select-all-users-checkbox"
                                />
                            </th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Name</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Email</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Role</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Joined</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((u) => (
                            <tr key={u.id} className={`border-t border-white/5 hover:bg-white/5 ${selectedUsers.includes(u.id) ? 'bg-primary/5' : ''}`}>
                                <td className="p-4">
                                    {u.role !== 'admin' ? (
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(u.id)}
                                            onChange={(e) => onSelectUser(u.id, e.target.checked)}
                                            className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                            data-testid={`select-user-${u.id}`}
                                        />
                                    ) : (
                                        <div className="w-4 h-4" title="Cannot select admin users" />
                                    )}
                                </td>
                                <td className="p-4 font-medium">{u.full_name}</td>
                                <td className="p-4 text-sm text-muted-foreground">{u.email}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 text-xs uppercase ${u.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-white/5'}`}>
                                        {u.role}
                                    </span>
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">
                                    {new Date(u.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center justify-end gap-1">
                                        {/* Login As button - only for Chairman and Admin */}
                                        {['tournament_chairman', 'admin'].includes(currentUser?.role) && 
                                         !['tournament_chairman', 'admin', 'tournament_director', 'admin_team'].includes(u.role) && (
                                            <Button 
                                                size="sm" 
                                                variant="ghost" 
                                                onClick={() => onLoginAs(u)} 
                                                className="text-primary hover:text-primary hover:bg-primary/20"
                                                title={`Login as ${u.full_name}`}
                                                data-testid={`login-as-${u.id}`}
                                            >
                                                <LogIn className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={() => onRoleChange(u)} className="text-muted-foreground hover:text-white">
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        {u.role !== 'admin' && (
                                            <Button size="sm" variant="ghost" onClick={() => onDelete(u.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredUsers.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">No users found</div>
                )}
            </div>
        </div>
    );
};

export default UsersTab;
